/**
 * Tailwind CSS implementation of PartnerLogo component
 * Uses Seeds partner logo CSS classes from partner-logo.css
 */

import React from "react";
import type { TypePartnerLogoProps } from "./PartnerLogoTypes";
import { usePartnerLogoModel } from "./usePartnerLogoModel";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const PartnerLogoTailwind = React.forwardRef<
  HTMLSpanElement,
  TypePartnerLogoProps
>(
  (
    {
      partnerName,
      backgroundType,
      logoType = "symbol",
      height,
      width,
      size,
      "aria-label": ariaLabel,
      svgProps,
      className,
      style: consumerStyle,
      ...rest
    }: TypePartnerLogoProps,
    ref: React.Ref<HTMLSpanElement>
  ) => {
    const {
      logo,
      logoViewBox,
      sizeValue,
      svgWidth,
      containerAriaProps,
      svgAriaProps,
    } = usePartnerLogoModel({
      partnerName,
      backgroundType,
      logoType,
      height,
      width,
      size,
      "aria-label": ariaLabel,
    });

    // CSS custom-property keys aren't part of CSSProperties — cast is required.
    const sizingStyle = {
      "--seeds-partner-logo-height": sizeValue,
      ...(svgWidth ? { "--seeds-partner-logo-svg-width": svgWidth } : {}),
    } as React.CSSProperties;

    return (
      <span
        ref={ref}
        className={cn("seeds-partner-logo", "logo", className)}
        data-qa-logo={partnerName}
        // sizing first so a consumer's unrelated style survives without
        // clobbering the computed size CSS variables
        style={{ ...sizingStyle, ...consumerStyle }}
        {...(rest as React.ComponentPropsWithoutRef<"span">)}
        {...containerAriaProps}
      >
        <svg
          className="logo-svg"
          viewBox={logoViewBox}
          focusable={false}
          data-qa-logo-svg={`${partnerName}-svg`}
          {...svgProps}
          {...svgAriaProps}
        >
          <use
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xlinkHref={`#seeds-svgs_${logo}`}
          />
        </svg>
      </span>
    );
  }
);

PartnerLogoTailwind.displayName = "PartnerLogoTailwind";
